(ns app.front.view.pdf-markup.core
  "PDF markup tool: attachment viewer and annotation editor built on
   EmbedPDF (https://www.embedpdf.com).

   EmbedPDF provides the PDF engine (PDFium wasm running in a worker), a
   plugin system and React components/hooks for the document stage. The
   viewer chrome (workspace, toolbars) is composed here in Reagent; the stage,
   its layers and the annotation machinery come from @embedpdf packages."
  (:require [ajax.core :refer [PUT]]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.icon-button :as ui-ib]
            [app.front.view.attachment.common :as attachment-common]
            [app.front.view.attachment.subs :as subs]
            [app.front.view.attachment.upload :as att-upload]
            [app.front.view.not-found :as not-found]
            [app.front.view.pdf-markup.drawing :as drawing]
            [app.front.view.pdf-markup.embedpdf :as epdf]
            [app.front.view.pdf-markup.stamp-picker :as stamp-picker]
            [app.front.view.pdf-markup.stamp-subs :as stamp-subs]
            [app.front.view.workspace.subs :as ws-subs]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;; Routed editor session state
;; ===============================

(defn- authority-access? [context attachment-id]
  ;; Match workspace/attachment review controls, not upload permission: applicants
  ;; may upload versions too, but this editor saves authority-markup versions.
  (-> (entities/find-entity context attachment-id)
      :entity/parent :granted-permissions :permission.application/review boolean))

(rf/reg-sub ::available?
  :<- [::entities/context]
  :<- [::ff/enabled? :feature-flag.id/pdf-markup-tool]
  (fn [[context enabled?] [_ attachment-id]]
    (and enabled? (authority-access? context attachment-id))))

(rf/reg-event-fx ::leave
  (fn [{:keys [db]} _]
    ;; TODO: Preserve resumable edits on exit once backend session persistence is wired up.
    {:db (dissoc db ::tool-session ::return-route)
     :fx [[:dispatch [::stamp-subs/clear]]]}))

(defn- workspace-route [workspace-id]
  {:route :view/workspace :params {:workspace-id workspace-id}})

(rf/reg-event-fx ::enter
  (fn [{:keys [db]} [_ {:keys [workspace-id] :as params}]]
    ;; TODO: Load or resume the persisted markup session here.
    (let [return-route (or (::return-route db) (workspace-route workspace-id))]
      {:db (-> db
               (dissoc ::return-route)
               (assoc ::tool-session (assoc params :session-id (random-uuid)
                                            :state :editing :return-route return-route)))
       :fx [[:dispatch [::stamp-subs/initialize workspace-id]]]})))

(defn- source-data [context {:keys [workspace-id attachment-id file-id] :as session}]
  (let [attachment (entities/find-entity context attachment-id)
        file       (entities/find-entity context file-id)
        ancestors  (eu/with-ancestors attachment)
        workspace  (p/find-first ancestors (eu/is-type-fn :entity.type/workspace))
        target     (p/find-first ancestors :process/state)]
    (when (and (= :entity.type/attachment (:entity/type attachment))
               (= workspace-id (:workspace/id workspace))
               (some #(contains? (du/entity-id-set (:attachment.version/files %)) file-id)
                     (:attachment/versions attachment))
               (= "application/pdf" (:attachment.version.file/content-type file))
               (authority-access? context attachment-id))
      (assoc session :process-target-id (:db/id target)
             :file (select-keys file [:db/id :attachment.version.file/filename])))))

(rf/reg-event-fx ::open-tool
  (fn [{:keys [db]} [_ attachment-id file-id]]
    (let [context      (entities/db->context db)
          attachment   (entities/find-entity context attachment-id)
          workspace-id (-> (eu/with-ancestors attachment)
                           (p/find-first (eu/is-type-fn :entity.type/workspace))
                           :workspace/id)
          params       (util/mmap workspace-id attachment-id file-id)
          match        (:route/match db)]
      (when (and (ff/enabled? :feature-flag.id/pdf-markup-tool)
                 (source-data context params))
        {:db         (assoc db ::return-route {:route  (get-in match [:data :name])
                                               :params (:path-params match)
                                               :query  (:query-params match)})
         :route/push {:route :view/pdf-markup :params params}}))))

(rf/reg-event-fx ::close
  (fn [{:keys [db]} _]
    ;; TODO: Terminate the persisted session here when backend sessions are wired up.
    (let [session (::tool-session db)]
      (when (= :editing (:state session))
        {:route/push (:return-route session)}))))

(rf/reg-sub ::tool-session
  :<- [::entities/context]
  :<- [::ff/enabled? :feature-flag.id/pdf-markup-tool]
  :<- [::session-data]
  (fn [[context enabled? session]]
    (when (and enabled? session)
      (source-data context session))))

(rf/reg-sub ::session-data :-> ::tool-session)

(rf/reg-sub ::session-state
  :<- [::session-data]
  :-> :state)

(rf/reg-sub ::view-data
  :<- [::session-data]
  :<- [:route/match]
  (fn [[session match]]
    (let [{:keys [route params query]} (or (:return-route session)
                                           (workspace-route (get-in match [:path-params :workspace-id])))]
      {:back-href (href/href route params query)})))

(defn filename-breadcrumb [{:keys [match]}]
  (let [params   (get-in match [:parameters :path])
        file     (<sub [::entities/entity (:file-id params)])
        filename (:attachment.version.file/filename file)]
    (when filename
      [:view/pdf-markup params
       [:span.inline-block.max-w-96.truncate.align-bottom {:title filename} filename]])))

;; Saving annotated document as a new attachment version
;; ======================================================

(rf/reg-sub ::saving?
  :<- [::session-state]
  (fn [state _]
    (boolean (#{:exporting :saving-in-background} state))))

(defn- current-session? [db session-id]
  (and session-id (= session-id (get-in db [::tool-session :session-id]))))

(rf/reg-fx ::export-document
  (fn [{:keys [documents] :as ctx}]
    (-> (js/Promise.resolve)
        ;; Preserve signed revisions; the engine still enforces certification restrictions.
        (.then #(.download ^js documents nil #js {:mode "incremental"}))
        (.then #(>evt [::save-version (-> ctx (dissoc :documents) (assoc :bytes %))]))
        (.catch #(>evt [::save-failed (:session-id ctx) %])))))

(rf/reg-event-fx ::export-started
  (fn [{:keys [db]} [_ ctx]]
    (when (and (current-session? db (:session-id ctx))
               (= :editing (get-in db [::tool-session :state]))
               (not (stamp-subs/busy? db))
               (ff/enabled? :feature-flag.id/pdf-markup-tool)
               (source-data (entities/db->context db) (::tool-session db)))
      {:db (assoc-in db [::tool-session :state] :exporting)
       :fx [[::export-document ctx]]})))

(rf/reg-event-fx ::save-version
  ;; bytes is a js/Uint8Array of the full annotated PDF, exported by the
  ;; EmbedPDF engine (save mode :incremental).
  (fn [{:keys [db]} [_ {:keys [attachment-id bytes session-id] :as ctx}]]
    ;; Export is complete: the upload owns the bytes and no longer needs the worker.
    ;; Only leave the editor that initiated this save, even if another one has since opened.
    (p/assoc-if {:db           (if (current-session? db session-id)
                                 (assoc-in db [::tool-session :state] :saving-in-background)
                                 db)
                 :comm/command {:command         :attachment/initialize-upload
                                :data            {:content-type  "application/pdf"
                                                  :md5-digest    (att-upload/md5 bytes)
                                                  :attachment-id attachment-id}
                                :on-success      [::upload-initialized ctx]
                                :on-done         [::save-failed session-id]
                                :on-unauthorized [::save-unauthorized session-id]
                                ;; comm's network-error path does not invoke :on-done.
                                :on-error        [::save-failed session-id]}}
                :route/push (when (current-session? db session-id) (:return-route ctx)))))

(rf/reg-event-fx ::upload-initialized
  (fn [_ [_ {:keys [bytes session-id] :as ctx} _ {:keys [file-id upload-url]}]]
    (PUT upload-url {:headers       {"Content-Type" "application/pdf"}
                     :body          (js/Blob. #js [bytes] #js {:type "application/pdf"})
                     :handler       #(>evt [::stored (assoc ctx :file-id file-id)])
                     :error-handler #(>evt [::save-failed session-id %])})
    {}))

(rf/reg-event-fx ::stored
  (fn [_ [_ {:keys [process-target-id attachment-id filename file-id session-id]}]]
    {:dispatch [::process/signal
                process-target-id
                [:process.signal/attachment-add-version
                 {:attachment-id     attachment-id
                  :file-id           file-id
                  :filename          filename
                  :content-type      "application/pdf"
                  :authority-markup? true}]
                {:notify     {:success :attachment/markup-version-saved}
                 :on-success [::save-success session-id]
                 :on-done    [::save-done session-id]
                 :on-error   [::save-done session-id]}]}))

(rf/reg-event-db ::save-success
  (fn [db [_ session-id]]
    (if (current-session? db session-id)
      (dissoc db ::tool-session)
      db)))

(rf/reg-event-db ::save-done
  (fn [db [_ session-id]]
    (if (current-session? db session-id)
      (assoc-in db [::tool-session :state] :editing)
      db)))

(rf/reg-event-fx ::save-unauthorized
  (fn [_ [_ session-id]]
    {:fx [[:dispatch [::save-done session-id]]
          [:dispatch [:session/session-expired]]]}))

(rf/reg-event-fx ::save-failed
  (fn [_ [_ session-id & error]]
    (js/console.error "EmbedPDF markup version save failed:" (pr-str error))
    {:fx [[:dispatch [::save-done session-id]]
          [:dispatch att-upload/upload-fail-notification]]}))

;; EmbedPDF setup
;; ==============

;; Plugins are plain values, but the Viewer treats the engine and plugin
;; identities as init-only, so they are constructed once and shared.
;; Wrapped in a delay so nothing calls into @embedpdf at namespace load:
;; the CLJS test builds replace the embedpdf wrapper namespace with a stub
;; (see app.front.view.pdf-markup.embedpdf and shadow-cljs.edn).
;; The signature tool is an annotation tool preset: freehand ink with pen-like
;; defaults, registered as its own tool id so its defaults stay independent
;; of the generic draw tool.
(def ^:private plugins
  (delay
    #js [(epdf/stagePlugin #js {:layout "vertical"})
         (epdf/renderPlugin)
         (epdf/interactionPlugin #js {:defaultTool "pointer"})
         (epdf/selectionPlugin)
         (epdf/annotationPlugin
           (clj->js {:tools (into drawing/filled-shape-presets
                                  [drawing/arrow-preset
                                   drawing/cloud-preset
                                   drawing/highlighter-preset
                                   {:id "stamp" :afterCreate {:tool "default"}}
                                   {:id       "signature"
                                    :extends  "ink"
                                    :defaults #js {:color       "#1a3f8f"
                                                   :strokeWidth 2.5}}])}))]))

(defn- make-engine
  "Engine thunk handed to the Viewer, which then owns the engine lifecycle:
   constructed on mount, destroyed on unmount. The worker and wasm load from
   the webpack-emitted assets."
  []
  (epdf/localEngine))

(defn- attachment-doc-source
  "Lazy OpenSource: fetches the attachment bytes through the normal
   attachment file API (session cookie included) when the viewer opens
   the document."
  [url file-id]
  (fn [abort-signal]
    (-> (js/fetch url #js {:signal abort-signal})
        (.then (fn [response]
                 (when-not (.-ok response)
                   (throw (js/Error. (str "Attachment fetch failed: " (.-status response)))))
                 (.arrayBuffer response)))
        (.then (fn [buffer]
                 #js {:kind  "bytes"
                      :id    (str "attachment-file-" file-id)
                      :bytes (js/Uint8Array. buffer)})))))

;; Viewer chrome
;; =============

(defn toolbar-actions [{:keys [tr on-end saving?]}]
  (r/with-let [*confirm-end? (r/atom false)]
    [:<>
     [ui/button {:kind     :tertiary
                 :size     :small
                 :disabled (boolean saving?)
                 :on-click #(reset! *confirm-end? true)}
      (tr [:attachment/end-markup-session])]
     [ui/confirm-modal
      {:id            "end-pdf-markup-session"
       :tr            tr
       :open?         @*confirm-end?
       :modal-title   (tr [:attachment/discard-markup-session])
       :yes-disabled? saving?
       :on-close      #(reset! *confirm-end? false)
       :on-yes-click  #(when-not saving? (on-end))}]]))

(defn- viewer-toolbar
  "Zoom, page navigation and saving over the EmbedPDF hooks. Must be
   rendered with :f> (hooks require a React function component)."
  [{:keys [tr can-save?] :as ctx}]
  (let [^js stage (epdf/useStage)
        ^js state (epdf/useStageState)
        ^js docs  (epdf/useDocuments)
        saving?   (<sub [::saving?])]
    [:div.flex.items-center.gap-1.px-2.py-1.bg-white.border-b.border-gray-300
     [ui-ib/icon-button {:icon        i/chevron-left
                         :description (tr [:previous])
                         :on-click    #(.previousPage stage)}]
     [:span.text-sm.whitespace-nowrap {:aria-live "polite"}
      (str (inc (.-currentPageIndex state)) " / " (.-pageCount state))]
     [ui-ib/icon-button {:icon        i/chevron-right
                         :description (tr [:next])
                         :on-click    #(.nextPage stage)}]
     [:span.mx-2.text-gray-300 "|"]
     [ui-ib/icon-button {:icon        i/zoom-out
                         :description (tr [:attachment/zoom-out])
                         :on-click    #(.zoomOut stage)}]
     [:span.text-sm.w-12.text-center (str (js/Math.round (* 100 (.-zoomLevel state))) "%")]
     [ui-ib/icon-button {:icon        i/zoom-in
                         :description (tr [:attachment/zoom-in])
                         :on-click    #(.zoomIn stage)}]
     (cc/button {:kind     :ghost
                 :size     :small
                 :on-click #(.fitWidth stage)}
                (tr [:attachment/fit-width]))
     (cc/button {:kind     :ghost
                 :size     :small
                 :on-click #(.fitPage stage)}
                (tr [:attachment/fit-page]))
     [:span.ml-auto]
     [toolbar-actions {:tr      tr
                       :saving? saving?
                       :on-end  #(>evt [::close])}]
     (when can-save?
       [ui/button {:kind          :primary
                   :size          :small
                   :icon          i/save
                   :icon-position :left
                   :disabled      (boolean (or saving? (<sub [::stamp-subs/busy?])))
                   :on-click      #(rf/dispatch-sync [::export-started (assoc ctx :documents docs)])}
        (tr [:attachment/save-markup-version])])]))

(def ^:private annotation-tools
  ;; [tool-id translation-key], nil = separator. Tool ids come from
  ;; @embedpdf/plugin-annotation's DEFAULT_TOOLS + our signature preset.
  [["pointer" :attachment/tool-select]
   ["pan" :attachment/tool-pan]
   nil
   ["highlight" :attachment/tool-highlight]
   ["free-text" :attachment/tool-text]
   nil
   ["signature" :attachment/tool-signature]])

(defn tool-button [{:keys [label title active? disabled? expanded? on-click]}]
  (cc/button (p/assoc-if {:kind         :ghost
                          :size         :small
                          :title        (or title label)
                          :aria-pressed (boolean active?)
                          :disabled     (boolean disabled?)
                          :class-name   (str "whitespace-nowrap "
                                             (when active?
                                               drawing/selected-tool-class))
                          :on-click     on-click}
                         :aria-expanded expanded?)
             label))

(defn- annotation-toolbar
  "The tool band and authority-group stamp picker. Applying a stamp snapshots its date, user and design."
  [{:keys [tr]}]
  (r/with-let [*anchor (atom nil)]
    (let [^js interaction        (epdf/useInteraction)
          ^js interaction-state  (epdf/useInteractionState)
          ^js annotation         (epdf/useAnnotation)
          active-id              (.-activeToolId interaction-state)
          {:keys [picker busy?]} (<sub [::stamp-subs/state])]
      [:div
       [:div.flex.items-center.gap-1.px-2.py-1.bg-white.border-b.border-gray-300.flex-wrap
        (->> annotation-tools
             (mapv (fn [entry]
                     (if-let [[tool-id label-key] entry]
                       [tool-button {:label     (tr [label-key])
                                     :active?   (= tool-id active-id)
                                     :disabled? busy?
                                     :on-click  #(do
                                                   (when-not (#{"pointer" "pan"} tool-id)
                                                     (.clear (.-selection annotation)))
                                                   (.activateTool interaction tool-id))}]
                       [:span.mx-1.text-gray-300 "|"])))
             (into [:<>]))
        [tool-button {:label     (tr [:attachment/tool-stamp])
                      :title     (tr [:attachment/tool-stamp-help])
                      :active?   (= "stamp" active-id)
                      :expanded? (boolean picker)
                      :disabled? (and busy? (not picker))
                      :on-click  (fn [event]
                                   (if picker
                                     (>evt [::stamp-subs/close])
                                     (do (.activateTool interaction "pointer")
                                         (reset! *anchor (.-currentTarget event))
                                         (>evt [::stamp-subs/open]))))}]
        [drawing/toolbar {:tr        tr
                          :active-id active-id
                          :disabled? busy?
                          :on-tool   #(when-not busy?
                                        (.clear (.-selection annotation))
                                        (.activateTool interaction %))}]]
       [:f> stamp-picker/picker {:tr tr :anchor @*anchor}]])))

(defn- annotation-selection-menu
  "Floating menu under the selected annotation(s)."
  [{:keys [tr]}]
  (let [^js annotation (epdf/useAnnotation)
        ^js state      (epdf/useAnnotationState)
        selected       (.-selected state)]
    [:div.flex.items-center.gap-1.p-1.bg-white.rounded.shadow-lg.border.border-gray-300
     (cc/button {:kind     :danger
                 :size     :small
                 :on-click #(.delete (.-selection annotation))}
                (str (tr [:delete])
                     (when (> (count selected) 1)
                       (str " (" (count selected) ")"))))
     (cc/button {:kind     :ghost
                 :size     :small
                 :on-click #(.clear (.-selection annotation))}
                (tr [:ok]))]))

(defn- document-stage [{:keys [tr] :as ctx}]
  [:div.flex.flex-col.h-full
   [:f> viewer-toolbar ctx]
   [:f> annotation-toolbar {:tr tr}]
   [:f> drawing/live-properties {:tr tr}]
   [:> epdf/Stage {:style   {:flex "1" :min-height 0}
                   :overlay (r/as-element
                              [:<>
                               [:> epdf/AnnotationMenu {:placement "bottom"}
                                [:f> annotation-selection-menu {:tr tr}]]
                               [:f> drawing/draft-menu {:tr tr}]])}
    (fn [] (r/as-element [:<>
                          ;; the annotation layer owns annotation rendering,
                          ;; so the page bitmap excludes them
                          [:> epdf/RenderLayer {:annotations false}]
                          [:> epdf/SelectionLayer]
                          [:> epdf/AnnotationLayer]]))]])

(defn- editable-document [{:keys [tr] :as ctx}]
  (let [^js annotation (epdf/useAnnotation)]
    (if (.canCreate annotation)
      [document-stage ctx]
      [ui/warning (tr [:attachment/pdf-certification-prevents-annotations])])))

(defn- viewer [{:keys [tr attachment-id process-target-id file session-id return-route]}]
  (let [file-id   (:db/id file)
        filename  (:attachment.version.file/filename file)
        url       (attachment-common/attachment-version-link attachment-id file-id)
        can-save? (boolean
                    (and process-target-id
                         (<sub [::process/signal-permitted?
                                process-target-id
                                [:process.signal/attachment-add-version
                                 {:attachment-id attachment-id}]])))
        loading   (r/as-element [:div.p-4.text-left.bg-white (tr [:loading])])]
    ^{:key session-id}
    [:> epdf/Viewer {:engine           make-engine
                     :plugins          @plugins
                     :initialDocuments [{:source (attachment-doc-source url file-id)
                                         :name   filename}]
                     :fallback         loading}
     [:> epdf/DocumentGate {:fallback loading
                            :locked   (fn [_]
                                        (r/as-element [ui/warning (tr [:attachment/pdf-password-protected])]))
                            :error    (fn [^js document]
                                        (js/console.error "EmbedPDF document load failed:" (.-error document))
                                        (r/as-element [ui/warning (tr [:attachment/pdf-load-failed])]))}
      [:f> editable-document
       (util/mmap tr attachment-id process-target-id filename can-save? session-id return-route)]]]))

(defn view [_]
  (let [session (<sub [::tool-session])
        tr      (<sub [::ws-subs/tr])]
    (cond
      (<sub [::ws-subs/loading?]) [cc/loading-spinner]
      session [:div.flex.flex-col.flex-1.min-h-0.pt-4.bg-white
               [viewer (assoc session :tr tr)]]
      :else [not-found/not-found-view])))

(defn open-pdf-markup-button
  "\"Open in PDF markup tool\" next to the regular attachment preview
   button. Only rendered for authority reviewers and PDF files with the flag enabled."
  [{:keys [id tr selected-version-id]}]
  (let [file (<sub [::subs/latest-file-for-version selected-version-id])]
    (when (and (<sub [::available? id])
               (= "application/pdf" (:attachment.version.file/content-type file)))
      (cc/button {:id         (str "open-pdf-markup-tool-btn-" id)
                  :kind       :tertiary
                  :class-name "whitespace-nowrap"
                  :on-click   #(>evt [::open-tool id (:db/id file)])}
                 (tr [:attachment/open-pdf-markup-tool])))))
